import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface SessionDetails {
  sessionId: string;
  customerName: string;
  agentName: string;
  startTime: string;
  status: 'active' | 'paused';
  description?: string;
}

interface CollaborationNote {
  id: string;
  author: string;
  content: string;
  timestamp: string;
  type: 'note' | 'suggestion' | 'issue';
}

export default function Collaborator() {
  const navigate = useNavigate();
  const [sessionCode, setSessionCode] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [hasJoinedSession, setHasJoinedSession] = useState(false);
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null);
  const [notes, setNotes] = useState<CollaborationNote[]>([
    {
      id: '1',
      author: 'Dr. Smith',
      content: 'Customer seems to be having trouble with the connection cable. Recommend checking the power adapter first.',
      timestamp: new Date(Date.now() - 300000).toISOString(),
      type: 'suggestion'
    },
    {
      id: '2',
      author: 'Tech Lead Johnson',
      content: 'This appears to be a Model X-200 device. Known issue with firmware version 2.1.3.',
      timestamp: new Date(Date.now() - 180000).toISOString(),
      type: 'issue'
    }
  ]);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<'note' | 'suggestion' | 'issue'>('note');

  const handleJoinSession = async () => {
    if (sessionCode.length !== 6) {
      alert('Please enter a valid 6-digit session code');
      return;
    }

    setIsJoining(true);
    
    // Simulate API call
    setTimeout(() => {
      // Mock session details
      setSessionDetails({
        sessionId: sessionCode,
        customerName: 'John Martinez',
        agentName: 'Sarah Wilson',
        startTime: new Date(Date.now() - 600000).toISOString(),
        status: 'active',
        description: 'Network connectivity issues with Router Model X-200'
      });
      setHasJoinedSession(true);
      setIsJoining(false);
    }, 1500);
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;

    const note: CollaborationNote = {
      id: Date.now().toString(),
      author: 'You',
      content: newNote,
      timestamp: new Date().toISOString(),
      type: noteType
    };

    setNotes(prev => [...prev, note]);
    setNewNote('');
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNoteTypeColor = (type: string) => {
    switch (type) {
      case 'suggestion': return 'bg-blue-100 text-blue-800';
      case 'issue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!hasJoinedSession) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Join as Collaborator</h1>
              <p className="text-gray-600">Enter the 6-digit session code to join as an expert collaborator</p>
            </div>

            <div className="mb-6">
              <label htmlFor="sessionCode" className="block text-sm font-medium text-gray-700 mb-2">
                Session Code
              </label>
              <input
                type="text"
                id="sessionCode"
                value={sessionCode}
                onChange={(e) => setSessionCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent tracking-widest"
                maxLength={6}
              />
            </div>

            <button
              onClick={handleJoinSession}
              disabled={sessionCode.length !== 6 || isJoining}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isJoining ? 'Joining Session...' : 'Join Session'}
            </button>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full text-gray-600 hover:text-gray-800 font-medium"
              >
                ← Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-800"
              >
                ← Dashboard
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Collaboration Session #{sessionDetails?.sessionId}
                </h1>
                <p className="text-sm text-gray-600">
                  {sessionDetails?.customerName} • Agent: {sessionDetails?.agentName}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                sessionDetails?.status === 'active' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {sessionDetails?.status?.toUpperCase()}
              </span>
              <span className="text-sm text-gray-500">
                Started {formatTime(sessionDetails?.startTime || '')}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Video Feed */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Live Session</h2>
                <p className="text-sm text-gray-600">{sessionDetails?.description}</p>
              </div>
              <div className="p-4">
                <div className="aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 6a2 2 0 012-2h6l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                      </svg>
                    </div>
                    <p className="text-lg font-semibold mb-1">Live Video Stream</p>
                    <p className="text-sm opacity-75">Observing agent-customer interaction</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Collaboration Tools */}
            <div className="mt-6 bg-white rounded-lg shadow-sm">
              <div className="p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Collaboration Tools</h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <button className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <svg className="w-6 h-6 text-blue-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    <span className="text-sm font-medium">Annotate</span>
                  </button>
                  <button className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <svg className="w-6 h-6 text-green-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                    <span className="text-sm font-medium">Share Screen</span>
                  </button>
                  <button className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <svg className="w-6 h-6 text-purple-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm font-medium">Send File</span>
                  </button>
                  <button className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <svg className="w-6 h-6 text-red-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span className="text-sm font-medium">Alert Agent</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Collaboration Notes */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Collaboration Notes</h3>
                <p className="text-sm text-gray-600">Share insights and suggestions</p>
              </div>
              
              {/* Add Note Form */}
              <div className="p-4 border-b">
                <div className="mb-3">
                  <select
                    value={noteType}
                    onChange={(e) => setNoteType(e.target.value as 'note' | 'suggestion' | 'issue')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="note">General Note</option>
                    <option value="suggestion">Suggestion</option>
                    <option value="issue">Issue/Warning</option>
                  </select>
                </div>
                <div className="mb-3">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add your insights or suggestions..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                  />
                </div>
                <button
                  onClick={handleAddNote}
                  disabled={!newNote.trim()}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
                >
                  Add Note
                </button>
              </div>

              {/* Notes List */}
              <div className="p-4 max-h-96 overflow-y-auto">
                <div className="space-y-4">
                  {notes.map((note) => (
                    <div key={note.id} className="border-l-4 border-blue-200 pl-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">{note.author}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getNoteTypeColor(note.type)}`}>
                            {note.type}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatTime(note.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{note.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
